import { useCallback, useMemo, useState } from 'react';
import type { ProcessedTicketData } from '../../types/processedTicket';
import type { TicketCardAction, TicketCardActionOption, TicketInfo } from '../../types/lottery';
import gamesService from '../../services/gamesService';
import { buildTicketBarcode, buildTicketDisplay, getTicketNumberForSoldCount } from '../../utils/ticketFactory';
import { formatDateMMDDYYYY } from '../../utils/formatUtils';
import { padLeftNumeric, parseBarcodePattern } from '../../utils/ticketFormatters';
import { formatPluAsUpcA, type UpcAFormatResult } from '../../utils/formatters/upcBarcodeFormatter';

type UseTicketCardLogicParams = {
  actionsDisabled?: boolean;
  pack: ProcessedTicketData;
  onPackReturn?: (packId: string) => void;
  storeId: string;
};

type TicketCardErrors = {
  saleError: string | null;
  returnError: string | null;
  returnPackError: string | null;
  freeTicketError: string | null;
};

type UseTicketCardLogicResult = {
  ticketsSold: number;
  ticketInfo: TicketInfo;
  formattedGameStartDate: string;
  formattedGameEndDate: string;
  formattedActiveDate: string;
  barcodeValue: string | null;
  currentTicketNumber: string;
  pluBarcode: UpcAFormatResult;
  actionOptions: TicketCardActionOption[];
  selectedAction: TicketCardAction;
  setSelectedAction: (action: TicketCardAction) => void;
  isActionDisabled: boolean;
  actionLoading: boolean;
  errors: TicketCardErrors;
  handleActionSubmit: () => Promise<void>;
};

const formatDateOnly = (value: string | Date): string => {
  if (!value) return 'N/A';
  try {
    return formatDateMMDDYYYY(value.toString());
  } catch (error) {
    return 'N/A';
  }
};

const getStoreKey = (storeId: string): string => {
  const storeData = localStorage.getItem('storeData');
  if (!storeData) return storeId;
  try {
    return JSON.parse(storeData).store_key || storeId;
  } catch (error) {
    return storeId;
  }
};

const digitsOnly = (value: string | number) => value.toString().replace(/\D/g, '');

export const useTicketCardLogic = ({ actionsDisabled = false, pack, onPackReturn, storeId }: UseTicketCardLogicParams): UseTicketCardLogicResult => {
  const [ticketsSold, setTicketsSold] = useState(pack.tickets_sold ?? pack.parsedInitialTicketsSold ?? 0);
  const [saleLoading, setSaleLoading] = useState(false);
  const [saleError, setSaleError] = useState<string | null>(null);
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState<string | null>(null);
  const [returnPackLoading, setReturnPackLoading] = useState(false);
  const [returnPackError, setReturnPackError] = useState<string | null>(null);
  const [freeTicketLoading, setFreeTicketLoading] = useState(false);
  const [freeTicketError, setFreeTicketError] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<TicketCardAction>('sale');

  // Canonical raw game id from pack payload; padding is applied based on barcode format.
  const apiGameNumber = useMemo(() => digitsOnly(pack.game_number), [pack.game_number]);
  const displayGameNumber = useMemo(() => {
    const defaultLength = 4;
    if (!pack.barcode_format) return padLeftNumeric(apiGameNumber, defaultLength).slice(-defaultLength);
    try {
      const pattern = parseBarcodePattern(pack.barcode_format);
      return padLeftNumeric(apiGameNumber, pattern.game.length).slice(-pattern.game.length);
    } catch {
      return padLeftNumeric(apiGameNumber, defaultLength).slice(-defaultLength);
    }
  }, [apiGameNumber, pack.barcode_format]);

  const ticketInfo = useMemo<TicketInfo>(() => {
    const ticketAtSoldCount = (sold: number) => getTicketNumberForSoldCount({
      firstTicketNumber: pack.baseTicketNumber,
      ticketsPerPack: pack.tickets_per_pack,
      ticketsSold: sold,
      direction: pack.direction,
    });
    const nextTicketNumber = ticketAtSoldCount(ticketsSold);
    const lastTicketNumber = pack.baseTicketNumber + pack.tickets_per_pack - 1;
    const isSoldOut = ticketsSold >= pack.tickets_per_pack;

    return {
      gameId: displayGameNumber || pack.parsedGameNumber,
      currentTicketNumber: ticketAtSoldCount(Math.max(0, ticketsSold - 1)),
      nextTicketNumber,
      lastTicketNumber,
      isSoldOut,
      ticketsSold,
      ticketsPerPack: pack.tickets_per_pack,
      startingTicketNumber: pack.starting_ticket_number,
    };
  }, [displayGameNumber, pack, ticketsSold]);

  const gameStartDate = useMemo(() => new Date(pack.date_activated), [pack.date_activated]);
  const gameEndDate = useMemo(() => {
    const endDate = new Date(gameStartDate);
    endDate.setDate(endDate.getDate() + 365);
    return endDate;
  }, [gameStartDate]);

  const formattedGameStartDate = useMemo(() => formatDateOnly(gameStartDate), [gameStartDate]);
  const formattedGameEndDate = useMemo(() => formatDateOnly(gameEndDate), [gameEndDate]);
  const formattedActiveDate = useMemo(() => formatDateOnly(pack.date_activated), [pack.date_activated]);

  const displayTicketNumber = ticketInfo.nextTicketNumber;

  const currentTicketNumber = useMemo(() => {
    if (ticketInfo.isSoldOut) return 'END';
    return buildTicketDisplay({
      format: pack.barcode_format,
      gameNumber: apiGameNumber,
      packId: pack.parsedPackId,
      randomDigit: pack.parsedRandomDigit,
      ticketNumber: displayTicketNumber,
    });
  }, [apiGameNumber, displayTicketNumber, pack.barcode_format, pack.parsedPackId, pack.parsedRandomDigit, ticketInfo.isSoldOut]);

  const barcodeValue = useMemo(() => {
    if (ticketInfo.isSoldOut) return null;
    if (!pack.barcode_format) return digitsOnly(currentTicketNumber);
    return buildTicketBarcode({
      format: pack.barcode_format,
      gameNumber: apiGameNumber,
      packId: pack.parsedPackId,
      ticketNumber: displayTicketNumber,
    });
  }, [apiGameNumber, currentTicketNumber, displayTicketNumber, pack.barcode_format, pack.parsedPackId, ticketInfo.isSoldOut]);

  const pluBarcode = useMemo(() => formatPluAsUpcA(pack.plu), [pack.plu]);

  const handleSale = useCallback(async () => {
    if (ticketInfo.isSoldOut || saleLoading) return;

    setSaleLoading(true);
    setSaleError(null);

    try {
      const actualStoreKey = getStoreKey(storeId);

      const saleRequest = {
        store_key: actualStoreKey,
        date_time: new Date().toISOString(),
        plu: pack.plu.toString(),
        qty: 1,
        rate: pack.price_point,
        total: pack.price_point,
        ticket_type: pack.pack_type,
        game_number: apiGameNumber || pack.parsedGameNumber,
        pack_number: pack.parsedPackId,
        ticket_number: ticketInfo.nextTicketNumber.toString(),
        is_free: false,
      };

      const response = await gamesService.recordSale(saleRequest);

      if (response.success) {
        setTicketsSold((prev) => prev + 1);
      } else {
        setSaleError(response.message || 'Failed to record sale');
      }
    } catch (error) {
      setSaleError('Failed to record sale. Please try again.');
    } finally {
      setSaleLoading(false);
    }
  }, [apiGameNumber, pack, saleLoading, storeId, ticketInfo.isSoldOut, ticketInfo.nextTicketNumber]);

  const handleFreeTicket = useCallback(async () => {
    if (ticketInfo.isSoldOut || freeTicketLoading) return;

    setFreeTicketLoading(true);
    setFreeTicketError(null);

    try {
      const actualStoreKey = getStoreKey(storeId);

      const freeTicketRequest = {
        store_key: actualStoreKey,
        date_time: new Date().toISOString(),
        plu: pack.plu.toString(),
        qty: 1,
        rate: pack.price_point,
        total: pack.price_point,
        ticket_type: pack.pack_type,
        game_number: apiGameNumber || pack.parsedGameNumber,
        pack_number: pack.parsedPackId,
        ticket_number: ticketInfo.nextTicketNumber.toString(),
        is_free: true,
      };

      const response = await gamesService.recordSale(freeTicketRequest);

      if (response.success) {
        setTicketsSold((prev) => prev + 1);
      } else {
        setFreeTicketError(response.message || 'Failed to record free ticket');
      }
    } catch (error) {
      setFreeTicketError('Failed to record free ticket. Please try again.');
    } finally {
      setFreeTicketLoading(false);
    }
  }, [apiGameNumber, freeTicketLoading, pack, storeId, ticketInfo.isSoldOut, ticketInfo.nextTicketNumber]);

  const handleReturn = useCallback(async () => {
    if (ticketsSold === 0 || returnLoading) return;

    setReturnLoading(true);
    setReturnError(null);

    try {
      const actualStoreKey = getStoreKey(storeId);

      const returnRequest = {
        store_key: actualStoreKey,
        date_time: new Date().toISOString(),
        plu: pack.plu.toString(),
        qty: 1,
        rate: pack.price_point,
        total: pack.price_point,
        ticket_type: pack.pack_type,
        game_number: apiGameNumber || pack.parsedGameNumber,
        pack_number: pack.parsedPackId,
        ticket_number: ticketInfo.currentTicketNumber.toString(),
      };

      const response = await gamesService.recordReturn(returnRequest);

      if (response.success) {
        setTicketsSold((prev) => prev - 1);
      } else {
        setReturnError(response.message || 'Failed to record return');
      }
    } catch (error) {
      setReturnError('Failed to record return. Please try again.');
    } finally {
      setReturnLoading(false);
    }
  }, [apiGameNumber, pack, returnLoading, storeId, ticketInfo.currentTicketNumber, ticketsSold]);

  const handleReturnPack = useCallback(async () => {
    if (returnPackLoading) return;

    setReturnPackLoading(true);
    setReturnPackError(null);

    try {
      const actualStoreKey = getStoreKey(storeId);

      const returnPackRequest = {
        store_key: actualStoreKey,
        pack_id: pack.pack_id,
        game_id: apiGameNumber || pack.parsedGameNumber,
        tickets_returned: pack.tickets_per_pack,
        returned_on: new Date().toISOString(),
        reason: 'Testing',
        pack_type: pack.pack_type,
      };

      const response = await gamesService.returnPack(returnPackRequest);

      if (response.success) {
        onPackReturn?.(pack.pack_id);
      } else {
        setReturnPackError(response.message || 'Failed to return pack');
      }
    } catch (error) {
      setReturnPackError('Failed to return pack. Please try again.');
    } finally {
      setReturnPackLoading(false);
    }
  }, [apiGameNumber, onPackReturn, pack.pack_id, pack.pack_type, pack.parsedGameNumber, pack.tickets_per_pack, returnPackLoading, storeId]);

  const actionOptions = useMemo<TicketCardActionOption[]>(() => {
    return [
      { value: 'sale', label: 'Sale', disabled: ticketInfo.isSoldOut || saleLoading },
      { value: 'return', label: 'Return', disabled: ticketsSold === 0 || returnLoading },
      { value: 'free', label: 'Free Ticket', disabled: ticketInfo.isSoldOut || freeTicketLoading },
      { value: 'returnPack', label: 'Return Pack', disabled: returnPackLoading },
    ];
  }, [freeTicketLoading, returnLoading, returnPackLoading, saleLoading, ticketInfo.isSoldOut, ticketsSold]);

  const isActionDisabled = useMemo(() => {
    const selectedOption = actionOptions.find((option) => option.value === selectedAction);
    return actionsDisabled || !selectedOption || selectedOption.disabled;
  }, [actionOptions, actionsDisabled, selectedAction]);

  const actionLoading = useMemo(() => {
    switch (selectedAction) {
      case 'sale':
        return saleLoading;
      case 'return':
        return returnLoading;
      case 'free':
        return freeTicketLoading;
      case 'returnPack':
        return returnPackLoading;
      default:
        return false;
    }
  }, [freeTicketLoading, returnLoading, returnPackLoading, saleLoading, selectedAction]);

  const handleActionSubmit = useCallback(async () => {
    if (actionsDisabled) return;

    switch (selectedAction) {
      case 'sale':
        await handleSale();
        break;
      case 'return':
        await handleReturn();
        break;
      case 'free':
        await handleFreeTicket();
        break;
      case 'returnPack':
        await handleReturnPack();
        break;
      default:
        break;
    }
  }, [actionsDisabled, handleFreeTicket, handleReturn, handleReturnPack, handleSale, selectedAction]);

  return {
    ticketsSold,
    ticketInfo,
    formattedGameStartDate,
    formattedGameEndDate,
    formattedActiveDate,
    barcodeValue: barcodeValue || null,
    currentTicketNumber,
    pluBarcode,
    actionOptions,
    selectedAction,
    setSelectedAction,
    isActionDisabled,
    actionLoading,
    errors: {
      saleError,
      returnError,
      returnPackError,
      freeTicketError,
    },
    handleActionSubmit,
  };
};
